import { faEdit } from '@fortawesome/pro-regular-svg-icons/faEdit'
import { faPercentage } from '@fortawesome/pro-regular-svg-icons/faPercentage'
import { NumberInput, Text, Flex, Select, Switch } from '@mantine/core'
import { useRouter } from 'next/router'
import React from 'react'

import { useAnalytics } from '@flecto/analytics'
import { queryClient } from '@flecto/core'
import {
  useQueryGetBookingById,
  useMutationUpdateBooking,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import type { CoreDiscountType, ParameterID } from '@flecto/types'
import { InputCurrency } from '@flecto/ui'
import { Icon } from '@flecto/ui'
import { BadgeEditable } from '@flecto/ui/@shared/BadgeEditable'
import { valueFromCents } from '@flecto/ui/lib/utils'

import classes from '../../styles.module.css'

type Props = {
  bookingId: ParameterID
  discount_cents: string
}

export const DiscountValue = ({ bookingId, discount_cents }: Props) => {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_sidebar' })
  const track = useAnalytics('segment')
  const [discountType, setDiscountType] = React.useState<string>(
    'discount_percentage'
  )
  const { refetch: refetchBooking, data } = useQueryGetBookingById({
    bookingId,
  })
  const [discountCents, setDiscountCents] = React.useState<number>(
    data?.discount_amount ?? 0
  )
  const [discountPercentage, setDiscountPercentage] = React.useState<number>(0)
  const [applyDiscount, setApplyDiscount] = React.useState(false)
  const router = useRouter()
  const id = Number(router.query.id)

  const disabledEdit =
    data?.status === 'completed' || data?.status === 'canceled'

  const { mutate } = useMutationUpdateBooking()

  const setDefaults = React.useCallback(() => {
    if (data?.discount_amount === null) return
    setApplyDiscount(true)
    if (data?.discount_type) {
      setDiscountType(data.discount_type)
    }
    if (data?.discount_type === 'discount_percentage') {
      setDiscountPercentage(data?.discount_amount)
      setDiscountCents(0)
    } else {
      const value = valueFromCents(data?.discount_amount ?? 0)
      setDiscountCents(value.cents)
      setDiscountPercentage(0)
    }
  }, [data])

  React.useEffect(() => {
    setDefaults()
  }, [data])

  const handleSubmit = React.useCallback(() => {
    if (!id) return

    const valueToSubmit =
      discountType === 'discount_percentage'
        ? (discountPercentage ?? 0)
        : discountCents

    mutate(
      {
        bookingId: id,
        booking: {
          discount_type: discountType as CoreDiscountType,
          discount_amount: applyDiscount ? valueToSubmit : 0,
        },
      },
      {
        onSuccess: () => {
          refetchBooking()
          queryClient.invalidateQueries(['bookings', 'read', bookingId])
          queryClient.invalidateQueries(['QueryGetBookingById', bookingId])

          track('BOOKING__DISCOUNT_APPLIED', {
            booking_id: bookingId,
            discountType,
            discountValue: valueToSubmit,
          })
        },
      }
    )
  }, [discountType, discountCents, discountPercentage, applyDiscount])

  return (
    <BadgeEditable
      value={discount_cents}
      className={classes.discount}
      rightSection={!disabledEdit && <Icon icon={faEdit} size='xs' ml='xs' />}
      popoverProps={{
        position: 'bottom-end',
        width: 260,
        withArrow: false,
        disabled: disabledEdit,
      }}
    >
      <BadgeEditable.Dropdown>
        <Flex gap={'sm'} direction='column' w={225}>
          <Flex align={'center'} justify={'space-between'} mb={'sm'}>
            <Text fw={500}>{t('overview.apply_discount')}</Text>
            <Switch
              checked={applyDiscount}
              onChange={(e) => setApplyDiscount(e.currentTarget.checked)}
            />
          </Flex>
          <Select
            label=''
            disabled={!applyDiscount}
            value={discountType}
            data={[
              {
                label: '% Subtotal',
                value: 'discount_percentage',
              },
              {
                label: '€ Euro',
                value: 'discount_value',
              },
            ]}
            onChange={(value) => {
              if (value) {
                setDiscountType(value)
              }
            }}
          />
          {discountType === 'discount_value' && (
            <InputCurrency
              disabled={!applyDiscount}
              placeholder='0,00'
              value={discountCents}
              onChange={setDiscountCents}
            />
          )}
          {discountType === 'discount_percentage' && (
            <NumberInput
              disabled={!applyDiscount}
              leftSection={<Icon icon={faPercentage} size='xs' />}
              min={0}
              max={100}
              hideControls
              value={
                discountPercentage !== undefined
                  ? discountPercentage
                  : (data?.discount_amount ?? 0)
              }
              onChange={(value) => setDiscountPercentage(Number(value))}
            />
          )}
        </Flex>
      </BadgeEditable.Dropdown>
      <BadgeEditable.Close mt={'sm'} onClick={setDefaults} />
      <BadgeEditable.Confirm mt={'sm'} onClick={handleSubmit} />
    </BadgeEditable>
  )
}
